/**
 * 💰 AffiliateProvisionenPage - Provisions-Übersicht
 */

import React from 'react';
import { AffiliateLayout } from '@/components/affiliate/layout/AffiliateLayout';
import { StatusBadge } from '@/components/affiliate/badges/StatusBadge';
import { SkeletonStatCard, SkeletonTable } from '@/components/affiliate/skeletons';
import { EmptyStateFilterNoResults, EmptyStateNoCommissions } from '@/components/affiliate/empty/EmptyState';
import { useAffiliateCommissions } from '@/features/affiliate/hooks';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { 
  Wallet, 
  TrendingUp, 
  Clock, 
  CheckCircle2,
  Download,
  Info
} from 'lucide-react';
import type { CommissionType, CommissionStatus } from '@/lib/affiliate-mock-data';

// ============================================
// MAIN PAGE
// ============================================

export const AffiliateProvisionenPage: React.FC = () => {
  const {
    commissions,
    summary,
    isLoading,
    filters,
    setTypeFilter,
    setStatusFilter,
    resetFilters,
    getStatusBadge,
    getTypeLabel,
  } = useAffiliateCommissions();

  // Loading State
  if (isLoading) {
    return (
      <AffiliateLayout
        pageTitle="Provisionen"
        pageSubtitle="Übersicht aller verdienten und ausstehenden Provisionen"
      >
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          {[1, 2, 3, 4].map(i => <SkeletonStatCard key={i} />)}
        </div>
        <SkeletonTable rows={6} columns={5} />
      </AffiliateLayout>
    );
  }

  const hasNoCommissions = commissions.length === 0 && filters.type === 'all' && filters.status === 'all';
  const hasNoResults = commissions.length === 0 && (filters.type !== 'all' || filters.status !== 'all');

  return (
    <AffiliateLayout
      pageTitle="Provisionen"
      pageSubtitle="Übersicht aller verdienten und ausstehenden Provisionen"
      headerActions={
        <Button variant="outline">
          <Download className="w-4 h-4 mr-2" />
          Export
        </Button>
      }
    >
      {/* Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <Card className="p-4 border-0 shadow-sm">
          <div className="flex items-center gap-3">
            <div className="p-2.5 bg-primary/10 rounded-xl">
              <Wallet className="w-5 h-5 text-primary" />
            </div>
            <div>
              <p className="text-sm text-gray-500">Lifetime Earnings</p>
              <p className="text-2xl font-bold">€{summary.totalEarned.toLocaleString('de-DE')}</p>
            </div>
          </div>
        </Card>
        <Card className="p-4 border-0 shadow-sm">
          <div className="flex items-center gap-3">
            <div className="p-2.5 bg-amber-100 rounded-xl">
              <Clock className="w-5 h-5 text-amber-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">Ausstehend</p>
              <p className="text-2xl font-bold text-amber-600">€{summary.pendingAmount.toLocaleString('de-DE')}</p>
            </div>
          </div>
        </Card>
        <Card className="p-4 border-0 shadow-sm">
          <div className="flex items-center gap-3">
            <div className="p-2.5 bg-green-100 rounded-xl">
              <CheckCircle2 className="w-5 h-5 text-green-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">Freigegeben</p>
              <p className="text-2xl font-bold text-green-600">€{summary.releasedAmount.toLocaleString('de-DE')}</p>
            </div>
          </div>
        </Card>
        <Card className="p-4 border-0 shadow-sm">
          <div className="flex items-center gap-3">
            <div className="p-2.5 bg-blue-100 rounded-xl">
              <TrendingUp className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">Ausgezahlt</p>
              <p className="text-2xl font-bold">€{summary.paidOutAmount.toLocaleString('de-DE')}</p>
            </div>
          </div>
        </Card>
      </div>

      {/* Info Banner */}
      <Card className="p-4 mb-6 bg-blue-50/80 border-blue-200">
        <div className="flex gap-3">
          <Info className="w-5 h-5 text-blue-600 shrink-0 mt-0.5" />
          <div>
            <p className="text-sm text-blue-900 font-medium">Provisionsmodell</p>
            <p className="text-sm text-blue-700 mt-1">
              <strong>Einmalig:</strong> €250 pro konvertiertem Restaurant • 
              <strong> Wiederkehrend:</strong> 1% vom Monatsumsatz • 
              <strong> Bonus:</strong> Level-Aufstieg und Specials
            </p>
          </div>
        </div>
      </Card>

      {/* Commission Table */}
      <Card className="border-0 shadow-sm">
        <CardHeader className="pb-3">
          <div className="flex flex-col sm:flex-row gap-4 justify-between items-start sm:items-center">
            <CardTitle className="text-base font-semibold">Transaktionen</CardTitle>
            <div className="flex gap-2">
              <Select value={filters.type} onValueChange={(v) => setTypeFilter(v as CommissionType | 'all')}>
                <SelectTrigger className="w-[140px]">
                  <SelectValue placeholder="Typ" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Alle Typen</SelectItem>
                  <SelectItem value="einmalig">Einmalig</SelectItem>
                  <SelectItem value="wiederkehrend">Wiederkehrend</SelectItem>
                  <SelectItem value="bonus">Bonus</SelectItem>
                </SelectContent>
              </Select>
              <Select value={filters.status} onValueChange={(v) => setStatusFilter(v as CommissionStatus | 'all')}>
                <SelectTrigger className="w-[140px]">
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Alle Status</SelectItem>
                  <SelectItem value="ausstehend">Ausstehend</SelectItem>
                  <SelectItem value="freigegeben">Freigegeben</SelectItem>
                  <SelectItem value="ausgezahlt">Ausgezahlt</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardHeader>
        <CardContent className="px-0">
          {/* Empty States */}
          {hasNoCommissions && (
            <div className="py-8">
              <EmptyStateNoCommissions />
            </div>
          )}
          
          {hasNoResults && (
            <div className="py-8">
              <EmptyStateFilterNoResults onReset={resetFilters} />
            </div>
          )}

          {commissions.length > 0 && (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-gray-50/50">
                    <TableHead className="font-semibold">Datum</TableHead>
                    <TableHead className="font-semibold">Beschreibung</TableHead>
                    <TableHead className="font-semibold">Typ</TableHead>
                    <TableHead className="font-semibold">Status</TableHead>
                    <TableHead className="text-right font-semibold">Betrag</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {commissions.map((commission) => {
                    const badge = getStatusBadge(commission.status);
                    return (
                      <TableRow key={commission.id}>
                        <TableCell className="text-gray-500">
                          {new Date(commission.date).toLocaleDateString('de-DE')}
                        </TableCell>
                        <TableCell className="font-medium">{commission.restaurant}</TableCell>
                        <TableCell>
                          <span className={`text-sm ${
                            commission.type === 'bonus' ? 'text-purple-600 font-medium' : 'text-gray-600'
                          }`}>
                            {getTypeLabel(commission.type)}
                          </span>
                        </TableCell>
                        <TableCell>
                          <StatusBadge variant={badge.variant} label={badge.label} size="sm" />
                        </TableCell>
                        <TableCell className="text-right font-semibold">
                          €{commission.amount.toLocaleString('de-DE', { minimumFractionDigits: 2 })}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </AffiliateLayout>
  );
};

export default AffiliateProvisionenPage;
